import React from "react";

import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";

import { useSort } from "@tzmedical/react-hooks";

import ColumnHeader from "../../components/common/ColumnHeader.jsx";
import EngineeringExpenseForm from "../../components/EngineeringExpenseForm.jsx";
import FormattedDate from "../../components/FormattedDate.jsx";
import ReadableCurrency from "../../components/ReadableCurrency.jsx";
import PageContext from "../../contexts/PageContext.jsx";

const sortOptions = {
  defaultSort: {
    field: "date",
    reverse: true,
  },
};

function EngineeringProjectExpenses({
  // Props
  project,
  mutate,
}) {
  const { editMode } = React.useContext(PageContext);
  const theme = useTheme();

  const [sortedExpenses, handleSortSelection, sort] = useSort(
    project?.engineeringExpenses || [],
    sortOptions
  );

  return (
    <Box sx={{ mx: 2 }}>
      <Grid container spacing={1}>
        <Grid size={12}>
          <Stack direction="row" spacing={1} sx={{ justifyContent: "center", alignItems: "center" }}>
            <Typography variant="h6">Project Expenses</Typography>
            <EngineeringExpenseForm project={project} mutate={mutate} data-cy="add-expense-button" />
          </Stack>
        </Grid>
        <Grid size="auto">
          <IconButton disabled>
            <Icon />
          </IconButton>
        </Grid>
        <Grid size={2} sx={{ display: { xs: "none", sm: "block" } }}>
          <ColumnHeader
            id="date"
            display="Date"
            sortField={sort.field}
            reverseSort={sort.reverse}
            onClick={handleSortSelection}
          />
        </Grid>
        <Grid size={3} sx={{ display: { xs: "none", sm: "block" } }}>
          <ColumnHeader
            id="vendor"
            display="Vendor"
            sortField={sort.field}
            reverseSort={sort.reverse}
            onClick={handleSortSelection}
          />
        </Grid>
        <Grid size="grow">
          <ColumnHeader
            id="description"
            display="Description"
            sortField={sort.field}
            reverseSort={sort.reverse}
            onClick={handleSortSelection}
          />
        </Grid>
        <Grid size={{ xs: 4, sm: 2 }}>
          <ColumnHeader
            id="amount"
            display="Amount"
            sortField={sort.field}
            reverseSort={sort.reverse}
            onClick={handleSortSelection}
          />
        </Grid>
      </Grid>
      <Divider variant="middle" color={theme.palette.primary.dark} sx={{ borderWidth: "1px", mx: -1 }} />
      {sortedExpenses.map((expense) => (
        <React.Fragment key={expense.id}>
          <Grid container spacing={1} sx={{ alignItems: "center" }}>
            <Grid size="auto">
              {editMode ? (
                <EngineeringExpenseForm project={project} expense={expense} mutate={mutate} />
              ) : (
                <IconButton disabled>
                  <Icon />
                </IconButton>
              )}
            </Grid>
            <Grid size={2} sx={{ display: { xs: "none", sm: "block" } }}>
              <Typography>
                <FormattedDate dateString={expense.date} />
              </Typography>
            </Grid>
            <Grid size={3} sx={{ display: { xs: "none", sm: "block" } }}>
              <Typography>{expense.vendor}</Typography>
            </Grid>
            <Grid size="grow">
              <Typography>{expense.description}</Typography>
            </Grid>
            <Grid size={{ xs: 4, sm: 2 }}>
              <ReadableCurrency value={expense.amount} />
            </Grid>
          </Grid>
          <Divider variant="middle" sx={{ mx: -1 }} />
        </React.Fragment>
      ))}
    </Box>
  );
}

export default EngineeringProjectExpenses;
